/* eslint-disable react-refresh/only-export-components --
   REQ-10 §9 legt fest, dass Provider und Hook in dieser Datei liegen. */

/**
 * Ersatz für den Singleton `Client/Services/SbBestellpositionContainerService.cs`: Der
 * Dienst hält im Altclient die Referenz auf `IwmSidebarBestellpositionContainer` und setzt
 * beim Öffnen zuerst `ContainerId`. In React übernimmt das ein Context.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { SidebarPosition } from '@/components/ui/types';

import { OrderPositionContainerSidebar } from './OrderPositionContainerSidebar';
import { useSidebar } from './useSidebar';

export interface OrderPositionContainerSidebarApi {
  show(containerId: number): void;
}

/**
 * Außerhalb des Providers bleibt `show` wirkungslos — genau wie
 * `SbOrderPositionContainerService.Show`, solange keine Seitenleiste angemeldet ist.
 * Die Bearbeitungs-Templates der `Container_*`-Spalten laufen auch in anderen Views.
 */
const INERT: OrderPositionContainerSidebarApi = {
  show: () => {
    /* keine angemeldete Seitenleiste */
  },
};

const OrderPositionContainerSidebarContext = createContext<OrderPositionContainerSidebarApi>(INERT);

export interface OrderPositionContainerSidebarProviderProps {
  position?: SidebarPosition;
  target?: string;
  onShow?(): void;
  onHide?(): void;
  children: ReactNode;
}

export function OrderPositionContainerSidebarProvider(
  props: OrderPositionContainerSidebarProviderProps,
) {
  const sidebar = useSidebar({
    onShow: () => {
      props.onShow?.();
    },
    onHide: () => {
      props.onHide?.();
    },
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method -- stabile Closure, keine Methode
  const { show } = sidebar;

  // Die Trigger-Komponente führt `show` in einer Abhängigkeitsliste; die Schnittstelle
  // muss deshalb über Renderzyklen hinweg dieselbe bleiben.
  const api = useMemo<OrderPositionContainerSidebarApi>(
    () => ({ show: (containerId) => show(containerId) }),
    [show],
  );

  return (
    <OrderPositionContainerSidebarContext.Provider value={api}>
      {props.children}
      <OrderPositionContainerSidebar
        open={sidebar.visible}
        containerId={sidebar.id}
        position={props.position}
        target={props.target}
        onHide={() => {
          sidebar.hide();
        }}
      />
    </OrderPositionContainerSidebarContext.Provider>
  );
}

export function useOrderPositionContainerSidebar(): OrderPositionContainerSidebarApi {
  return useContext(OrderPositionContainerSidebarContext);
}

export default OrderPositionContainerSidebarProvider;
